import type { IEventStore } from "applesauce-core/event-store";
import { getOutboxes, type Filter } from "applesauce-core/helpers";
import type { RelayPool } from "applesauce-relay";
import type { NostrEvent } from "nostr-tools";

import {
  requestPreWriteRelaySnapshot,
  requiredFallbackVotes,
  requiredPersonalOutboxVotes,
} from "@/lib/preWriteRelaySnapshot";
import { normalizeUrl } from "@/lib/url";

const MAILBOX_KIND = 10_002;
const GLOBAL_VANISH_KIND = 62;
const MAX_POLICY_PASSES = 3;

export interface PersonalRelayPlan {
  contributors: string[];
  voters: string[];
  requiredVotes: number;
  voterLabel: string;
}

export interface PersonalReplaceableTarget {
  pubkey: string;
  kind: number;
  identifier?: string;
}

export interface PersonalReplaceableSnapshot {
  winner: NostrEvent | undefined;
  candidates: NostrEvent[];
  deletions: NostrEvent[];
  vanishes: NostrEvent[];
  quorumRelays: string[];
}

export interface PersonalRelayListSnapshot extends PersonalReplaceableSnapshot {
  relayPlan: PersonalRelayPlan;
}

interface SnapshotOptions {
  pool: RelayPool;
  store: IEventStore;
  target: PersonalReplaceableTarget;
  plan: PersonalRelayPlan;
  maxSettleTime: number;
}

function normalizedRelays(relays: readonly string[]): string[] {
  return [...new Set(relays.map(normalizeUrl))].sort();
}

function targetFilter(target: PersonalReplaceableTarget): Filter {
  return {
    kinds: [target.kind],
    authors: [target.pubkey],
    ...(target.identifier === undefined ? {} : { "#d": [target.identifier] }),
  } as Filter;
}

function targetCoordinate(
  target: PersonalReplaceableTarget,
): string | undefined {
  return target.identifier === undefined
    ? undefined
    : `${target.kind}:${target.pubkey}:${target.identifier}`;
}

function isTargetEvent(
  event: NostrEvent,
  target: PersonalReplaceableTarget,
): boolean {
  return (
    event.kind === target.kind &&
    event.pubkey === target.pubkey &&
    (target.identifier === undefined ||
      event.tags.some(
        ([name, value]) => name === "d" && value === target.identifier,
      ))
  );
}

function latestEvent(events: Iterable<NostrEvent>): NostrEvent | undefined {
  let latest: NostrEvent | undefined;
  for (const event of events) {
    if (
      !latest ||
      event.created_at > latest.created_at ||
      (event.created_at === latest.created_at && event.id < latest.id)
    ) {
      latest = event;
    }
  }
  return latest;
}

function isGlobalVanish(event: NostrEvent, pubkey: string): boolean {
  return (
    event.kind === GLOBAL_VANISH_KIND &&
    event.pubkey === pubkey &&
    event.tags.some(
      ([name, value]) => name === "relay" && value === "ALL_RELAYS",
    )
  );
}

function invalidatesCandidate(
  candidate: NostrEvent,
  deletions: readonly NostrEvent[],
  vanishes: readonly NostrEvent[],
  coordinate: string | undefined,
): boolean {
  return (
    deletions.some(
      (deletion) =>
        deletion.kind === 5 &&
        deletion.pubkey === candidate.pubkey &&
        deletion.created_at >= candidate.created_at &&
        deletion.tags.some(
          ([name, value]) =>
            (name === "e" && value === candidate.id) ||
            (coordinate !== undefined && name === "a" && value === coordinate),
        ),
    ) ||
    vanishes.some(
      (vanish) =>
        isGlobalVanish(vanish, candidate.pubkey) &&
        vanish.created_at >= candidate.created_at,
    )
  );
}

function plansEqual(left: PersonalRelayPlan, right: PersonalRelayPlan) {
  return (
    left.requiredVotes === right.requiredVotes &&
    left.voters.length === right.voters.length &&
    left.voters.every((relay, index) => relay === right.voters[index])
  );
}

export function personalRelayPlan(
  outboxRelays: readonly string[],
  lookupRelays: readonly string[],
  fallbackRelays: readonly string[],
): PersonalRelayPlan {
  const outboxes = normalizedRelays(outboxRelays);
  const lookups = normalizedRelays(lookupRelays);
  const fallbacks = normalizedRelays(fallbackRelays);
  if (outboxes.length > 0) {
    return {
      contributors: normalizedRelays([...lookups, ...fallbacks]),
      voters: outboxes,
      requiredVotes: requiredPersonalOutboxVotes(outboxes.length),
      voterLabel: "NIP-65 outbox",
    };
  }
  if (fallbacks.length > 0) {
    return {
      contributors: lookups,
      voters: fallbacks,
      requiredVotes: requiredFallbackVotes(fallbacks.length),
      voterLabel: "fallback relay",
    };
  }
  return {
    contributors: [],
    voters: lookups,
    requiredVotes: requiredFallbackVotes(lookups.length),
    voterLabel: "user-index relay",
  };
}

/** Refresh one exact personal coordinate and its deletion/vanish evidence. */
export async function requestPersonalReplaceableSnapshot({
  pool,
  store,
  target,
  plan,
  maxSettleTime,
}: SnapshotOptions): Promise<PersonalReplaceableSnapshot> {
  if (plan.requiredVotes === 0) {
    throw new Error(
      "No authoritative relay is configured for the personal freshness check.",
    );
  }
  const deadline = Date.now() + maxSettleTime;
  const filter = targetFilter(target);
  const coordinate = targetCoordinate(target);
  const vanishFilter = {
    kinds: [GLOBAL_VANISH_KIND],
    authors: [target.pubkey],
  } as Filter;
  const addressDeletionFilters = coordinate
    ? ([
        {
          kinds: [5],
          authors: [target.pubkey],
          "#a": [coordinate],
        } as Filter,
      ] as Filter[])
    : [];
  const initial = await requestPreWriteRelaySnapshot({
    pool,
    store,
    filters: [filter, ...addressDeletionFilters, vanishFilter],
    contributorRelays: plan.contributors,
    voterRelays: plan.voters,
    requiredVotes: plan.requiredVotes,
    maxSettleTime,
    voterLabel: plan.voterLabel,
  });
  const candidateMap = new Map<string, NostrEvent>();
  for (const event of [
    ...(store.getByFilters(filter) as NostrEvent[]),
    ...initial.events,
  ]) {
    if (isTargetEvent(event, target)) candidateMap.set(event.id, event);
  }

  const deletionMap = new Map<string, NostrEvent>();
  const vanishMap = new Map<string, NostrEvent>();
  const collectEvidence = (events: Iterable<NostrEvent>) => {
    for (const event of events) {
      if (event.kind === 5 && event.pubkey === target.pubkey) {
        deletionMap.set(event.id, event);
      } else if (isGlobalVanish(event, target.pubkey)) {
        vanishMap.set(event.id, event);
      }
    }
  };
  collectEvidence(initial.events);
  collectEvidence(store.getByFilters(vanishFilter) as NostrEvent[]);
  for (const deletionFilter of addressDeletionFilters) {
    collectEvidence(store.getByFilters(deletionFilter) as NostrEvent[]);
  }

  const candidateIds = [...candidateMap.keys()];
  if (candidateIds.length > 0) {
    const exactDeletionFilter = {
      kinds: [5],
      authors: [target.pubkey],
      "#e": candidateIds,
    } as Filter;
    const exact = await requestPreWriteRelaySnapshot({
      pool,
      store,
      filters: [exactDeletionFilter],
      contributorRelays: plan.contributors,
      voterRelays: initial.quorumRelays,
      requiredVotes: initial.quorumRelays.length,
      maxSettleTime: Math.max(0, deadline - Date.now()),
      voterLabel: `${plan.voterLabel} deletion`,
    });
    collectEvidence(exact.events);
    collectEvidence(store.getByFilters(exactDeletionFilter) as NostrEvent[]);
  }

  const candidates = [...candidateMap.values()];
  const deletions = [...deletionMap.values()];
  const vanishes = [...vanishMap.values()];
  const candidate = latestEvent(candidates);
  return {
    winner:
      candidate &&
      !invalidatesCandidate(candidate, deletions, vanishes, coordinate)
        ? candidate
        : undefined,
    candidates,
    deletions,
    vanishes,
    quorumRelays: initial.quorumRelays,
  };
}

export interface PersonalRelayListSnapshotOptions {
  pool: RelayPool;
  store: IEventStore;
  pubkey: string;
  lookupRelays: readonly string[];
  fallbackRelays: readonly string[];
  maxSettleTime: number;
}

/** Refresh NIP-65 before deciding whether an account has declared outboxes. */
export async function requestPersonalRelayListSnapshot({
  pool,
  store,
  pubkey,
  lookupRelays,
  fallbackRelays,
  maxSettleTime,
}: PersonalRelayListSnapshotOptions): Promise<PersonalRelayListSnapshot> {
  const deadline = Date.now() + maxSettleTime;
  const cached = store.getReplaceable(MAILBOX_KIND, pubkey);
  let plan = personalRelayPlan(
    cached ? getOutboxes(cached) : [],
    lookupRelays,
    fallbackRelays,
  );

  for (let pass = 0; pass < MAX_POLICY_PASSES; pass += 1) {
    const snapshot = await requestPersonalReplaceableSnapshot({
      pool,
      store,
      target: { pubkey, kind: MAILBOX_KIND },
      plan,
      maxSettleTime: Math.max(0, deadline - Date.now()),
    });
    const nextPlan = personalRelayPlan(
      snapshot.winner ? getOutboxes(snapshot.winner) : [],
      lookupRelays,
      fallbackRelays,
    );
    if (plansEqual(plan, nextPlan)) {
      return { ...snapshot, relayPlan: plan };
    }
    plan = nextPlan;
  }

  throw new Error(
    "The NIP-65 relay policy kept changing during the bounded freshness check.",
  );
}

export interface PersonalSnapshotWithDiscoveryOptions {
  pool: RelayPool;
  store: IEventStore;
  ownerPubkey: string;
  target: PersonalReplaceableTarget;
  lookupRelays: readonly string[];
  fallbackRelays: readonly string[];
  /** Extra discovery sources whose EOSE never votes on the owner's state. */
  contributorRelays?: readonly string[];
  maxSettleTime: number;
}

/** Resolve the owner's mailboxes, then refresh a target under that policy. */
export async function requestPersonalSnapshotWithDiscovery({
  pool,
  store,
  ownerPubkey,
  target,
  lookupRelays,
  fallbackRelays,
  contributorRelays = [],
  maxSettleTime,
}: PersonalSnapshotWithDiscoveryOptions): Promise<
  PersonalReplaceableSnapshot & { relayPlan: PersonalRelayPlan }
> {
  const deadline = Date.now() + maxSettleTime;
  const relayList = await requestPersonalRelayListSnapshot({
    pool,
    store,
    pubkey: ownerPubkey,
    lookupRelays,
    fallbackRelays,
    maxSettleTime,
  });
  if (target.pubkey === ownerPubkey && target.kind === MAILBOX_KIND) {
    return relayList;
  }
  const targetPlan = {
    ...relayList.relayPlan,
    contributors: normalizedRelays([
      ...relayList.relayPlan.contributors,
      ...contributorRelays,
    ]),
  };
  const snapshot = await requestPersonalReplaceableSnapshot({
    pool,
    store,
    target,
    plan: targetPlan,
    maxSettleTime: Math.max(0, deadline - Date.now()),
  });
  return { ...snapshot, relayPlan: targetPlan };
}
